import axios from 'axios';
import type {
  Enquiry,
  EnquiryFormData,
  EnquiryListResponse,
  EnquiryUpdateData
} from '../types/enquiry';
import { fetchAuthSession } from 'aws-amplify/auth';

// API Base URL - will be set from environment variable after deployment
const API_BASE_URL = import.meta.env.VITE_ENQUIRIES_API_URL || 'https://YOUR_API_GATEWAY_URL/prod';

class EnquiryService {
  /**
   * Create a new enquiry (PUBLIC - No authentication required)
   */
  async createEnquiry(formData: EnquiryFormData): Promise<{ enquiryId: string; enquiry: Enquiry }> {
    try {
      const response = await axios.post(`${API_BASE_URL}/enquiries`, formData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error creating enquiry:', error);
      throw this.handleError(error);
    }
  }

  /**
   * List all enquiries with optional filters (PROTECTED - Requires authentication)
   */
  async listEnquiries(params?: {
    status?: string;
    limit?: number;
    lastKey?: string;
    search?: string;
  }): Promise<EnquiryListResponse> {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();

      if (params?.status) queryParams.append('status', params.status);
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.lastKey) queryParams.append('lastKey', params.lastKey);
      if (params?.search) queryParams.append('search', params.search);

      const url = `${API_BASE_URL}/enquiries${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

      const response = await axios.get(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error listing enquiries:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get a single enquiry by ID (PROTECTED - Requires authentication)
   */
  async getEnquiry(enquiryId: string): Promise<Enquiry> {
    try {
      const token = await this.getAuthToken();
      const response = await axios.get(`${API_BASE_URL}/enquiries/${enquiryId}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error getting enquiry:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Update an enquiry (PROTECTED - Requires authentication)
   */
  async updateEnquiry(enquiryId: string, updateData: EnquiryUpdateData): Promise<Enquiry> {
    try {
      const token = await this.getAuthToken();
      const response = await axios.put(
        `${API_BASE_URL}/enquiries/${enquiryId}`,
        updateData,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      return response.data.enquiry;
    } catch (error) {
      console.error('Error updating enquiry:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Delete an enquiry (PROTECTED - Requires authentication)
   */
  async deleteEnquiry(enquiryId: string): Promise<void> {
    try {
      const token = await this.getAuthToken();
      await axios.delete(`${API_BASE_URL}/enquiries/${enquiryId}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
    } catch (error) {
      console.error('Error deleting enquiry:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get authentication token from Cognito
   */
  private async getAuthToken(): Promise<string> {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) {
        throw new Error('No authentication token available');
      }
      return token;
    } catch (error) {
      throw new Error('Authentication required. Please log in.');
    }
  }

  /**
   * Handle API errors
   */
  private handleError(error: any): Error {
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.error || error.message || 'An error occurred';
      return new Error(message);
    }
    return error instanceof Error ? error : new Error('Unknown error occurred');
  }

  /**
   * Export enquiries to CSV (client-side) - Includes all fields
   */
  exportToCSV(enquiries: Enquiry[]): void {
    const headers = [
      'Enquiry ID',
      'Created At',
      'Updated At',
      'Status',
      'Priority',
      'Full Name',
      'Email',
      'Phone',
      'Preferred Call Time',
      'Preferred Contact Method',
      'Course Interest',
      'Primary Goal',
      'Education Level',
      'Field of Study',
      'Graduation Year',
      'Background',
      'Years of Experience',
      'Current Role',
      'Current Company',
      'Domain',
      'Companies Worked',
      'Location',
      'Current CTC',
      'Prior Experience',
      'Skill Level',
      'Learning Mode',
      'Preferred Session Time',
      'Class Preference',
      'Training Mode',
      'Specific Topics',
      'Additional Comments',
      'Source',
      'UTM Source',
      'UTM Medium',
      'UTM Campaign',
      'Assigned To',
      'Tags',
      'Follow-ups Count',
      'Notes',
    ];

    const rows = enquiries.map((e) => [
      e.enquiryId,
      new Date(e.createdAt).toLocaleDateString(),
      new Date(e.updatedAt).toLocaleDateString(),
      e.status,
      e.priority,
      e.fullName,
      e.email || '',
      e.phone || '',
      e.preferredCallTime || '',
      e.preferredContactMethod || '',
      e.courseInterest,
      e.primaryGoal || '',
      e.education?.level || '',
      e.education?.field || '',
      e.education?.graduationYear || '',
      e.background,
      e.experience?.years || '',
      e.experience?.currentRole || '',
      e.experience?.currentCompany || '',
      e.experience?.domain || '',
      e.experience?.companiesWorked || '',
      e.location,
      e.currentCTC || '',
      (e.priorExperience || []).join('; '),
      e.skillLevel || '',
      e.learningPreferences?.mode || '',
      e.learningPreferences?.time || '',
      e.learningPreferences?.classPreference || '',
      e.learningPreferences?.trainingMode || '',
      e.specificTopics || '',
      e.additionalComments || '',
      e.source || '',
      e.utmSource || '',
      e.utmMedium || '',
      e.utmCampaign || '',
      e.assignedTo || '',
      (e.tags || []).join('; '),
      e.followUps?.length || 0,
      e.notes || '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `enquiries_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

export const enquiryService = new EnquiryService();
